import { useApiClient, useFetch } from '@marageti/z4-lib';
import { TripRequest } from '@marageti/z4-sdk/lib/travel';
import { TeamMember } from '@marageti/z4-sdk/lib/people';
import { useSnackbar } from 'notistack';
import { RoleName, UserStatus } from '@marageti/z4-sdk/lib/auth';
import { SearchRequest, SearchResult } from '@marageti/z4-sdk/lib/entity';
import { useAuthStore } from '../../store';

const teamMemberSearch: SearchRequest = {
  filterBy: [{ field: 'userInfo.roles', value: [RoleName.CARE] }, { field: 'userInfo.status', value: [UserStatus.ENABLED] }],
  pagination: { limit: 1000, page: 1 },
};

type UseTripRequestAssignmentProps = {
  tripRequestId: string | undefined
};

const useTripRequestAssignment = ({
  tripRequestId,
}: UseTripRequestAssignmentProps) => {
  const apiClient = useApiClient();
  const { loggedInTeamMember } = useAuthStore();
  const { enqueueSnackbar } = useSnackbar();

  const { data: teamMembers } = useFetch<SearchResult<TeamMember>>(apiClient.peopleClient.postTeamMemberSearch, [teamMemberSearch], true);
  const tripRequest = useFetch<TripRequest>(apiClient.tripRequestsClient.getTripRequest, [tripRequestId], !!tripRequestId);

  const handleAssignError = () => {
    enqueueSnackbar('Something went wrong on our end. Please try again.', { variant: 'default' });
  };

  const assignToTeamMember = async (teamMember: TeamMember) => {
    try {
      if (tripRequestId) {
        await apiClient.tripRequestsClient.putTripRequestAssignment(tripRequestId, {
          id: tripRequestId,
          assignedToName: teamMember.fullName,
          assignedTo: teamMember.id,
        });

        tripRequest.mutate();
      }
    } catch (error) {
      handleAssignError();
    }
  };

  const assignToSelf = async () => {
    try {
      if (tripRequestId && loggedInTeamMember) {
        await apiClient.tripRequestsClient.putTripRequestAssignment(tripRequestId, {
          id: tripRequestId,
          assignedToName: loggedInTeamMember.fullName,
          assignedTo: loggedInTeamMember.id,
        });

        tripRequest.mutate();
      }
    } catch (error) {
      handleAssignError();
    }
  };

  const unassign = async () => {
    try {
      if (tripRequestId) {
        await apiClient.tripRequestsClient.putTripRequestAssignment(tripRequestId, {
          id: tripRequestId,
          assignedToName: '',
          assignedTo: '',
        });

        tripRequest.mutate();
      }
    } catch (error) {
      handleAssignError();
    }
  };

  return {
    teamMembers: teamMembers?.hits || [],
    tripRequest,
    assignToTeamMember,
    assignToSelf,
    unassign,
  };
};

export default useTripRequestAssignment;
